import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import html2pdf from 'html2pdf.js';
import { Card, message } from 'antd'; 
import OfferLetterService from "../../Services/Manage/OfferLetterService";

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ],
};

function GenerateOfferLetter({ user_id, departmentId, addedBy, department, name }) {
  const defaultOfferLetterTemplate = `
    <div style="font-size: 20px; line-height: 2; font-family: 'Georgia', serif; padding: 40px; max-width: 700px; margin: auto;">
      <p>Dear <strong>[Candidate Name]</strong>,</p>
      <p>We are pleased to offer you the position of <strong>[Job Title]</strong> at <strong>[Company Name]</strong>. Your skills and experience stood out during the selection process, and we believe you will make a significant contribution to our team.</p>
      <p>Your start date will be <strong>[Start Date]</strong>, with an annual salary of <strong>[Salary]</strong>.</p>
      <p>In your role, you will be expected to demonstrate professionalism, teamwork, and a commitment to excellence. We encourage all employees to bring forward new ideas and actively participate in company initiatives that foster growth and innovation.</p>
      <p>Our company culture values integrity, respect, and collaboration. We believe these principles will support you in achieving both personal and professional success while contributing positively to the workplace environment.</p>
      <p>During your initial period with us, you will receive training and support to ensure you have all the resources needed to excel in your responsibilities.</p>
      <br/>
      <p>We are excited about the prospect of you joining our organization and look forward to a mutually rewarding relationship. Your expertise will be instrumental as we work together towards achieving our strategic goals.</p>
      <br/>
      <p>Sincerely,</p>
      <p><strong>[Your Name]</strong><br />[Your Position]</p>
    </div>
  `;

  const getPersonalizedTemplate = () => {
    return defaultOfferLetterTemplate
      .replace('[Candidate Name]', name || 'Candidate')
      .replace('[Job Title]', department || 'Job Title')
      .replace('[Company Name]', 'Your Company')
      .replace('[Start Date]', '01/01/2025')
      .replace('[Salary]', 'XXX')
      .replace('[Your Name]', addedBy || 'HR Team')
      .replace('[Your Position]', 'HR Manager');
  };

  const [text, setText] = useState(getPersonalizedTemplate());
  const [loading, setLoading] = useState(false);
  const [isExistingLetter, setIsExistingLetter] = useState(false); 
  const [isSaved, setIsSaved] = useState(false);
  const printableRef = useRef();

  useEffect(() => {
    if (!user_id) return;

    const fetchOfferLetter = async () => {
      setLoading(true);
      try {
        const data = await OfferLetterService.getOfferLetterByUserId(user_id);
        if (data && data.description) {
          setText(data.description);
          setIsExistingLetter(true);
          setIsSaved(true);
        } else {
          setText(getPersonalizedTemplate());
          setIsExistingLetter(false);
          setIsSaved(false);
        }
      } catch (error) {
        message.error("Failed to fetch offer letter.");
        setText(getPersonalizedTemplate());
        setIsExistingLetter(false);
        setIsSaved(false);
      } finally {
        setLoading(false);
      }
    };

    fetchOfferLetter();
  }, [user_id, name, department, addedBy]);

  const handleSaveOfferLetter = async () => {
    if (!user_id) {
      message.error("User ID is missing!");
      return;
    }

    setLoading(true);
    const payload = {
      emp_id: user_id,  
      description: text,
      added_by: addedBy,
    };

    try {
      if (isExistingLetter) {
        await OfferLetterService.updateOfferLetter(user_id, payload);
        message.success("Offer letter updated successfully.");
      } else {
        await OfferLetterService.createOfferLetter(payload);
        message.success("Offer letter created successfully.");
        setIsExistingLetter(true);
      }
      setIsSaved(true);
    } catch (error) {
      message.error("Failed to save offer letter.");
      setIsSaved(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const element = printableRef.current;
    if (!element) return;

    const opt = {
      margin: 0.5,
      filename: `${name || 'Candidate'}_${user_id || 'Unknown'}_offerletter.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <Card title="Generate Offer Letter" loading={loading}>
      <ReactQuill
        value={text}
        onChange={setText}
        modules={modules}
        theme="snow"
        style={{ marginBottom: 20 }}
      />

      <button
        onClick={handleSaveOfferLetter}
        style={{
          padding: '10px 20px',
           backgroundColor: '#1890ff',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          marginRight: 10,
          marginBottom: 20
        }}
        disabled={loading}
      >
        Save Offer Letter
      </button>

      <button
        onClick={handleDownloadPDF}
        style={{
          padding: '10px 20px',
          backgroundColor: isSaved ? '#1890ff' : '#888',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: isSaved ? 'pointer' : 'not-allowed',
          marginBottom: 20
        }}
        disabled={!isSaved || loading}
      >
        Download as PDF
      </button>

      <div style={{ display: 'none' }}>
        <div ref={printableRef} dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </Card>
  );
}

export default GenerateOfferLetter;
